/// <reference lib="es2023.array" />
import { ChartConfiguration, ChartOptions } from 'chart.js';
import { DateTime } from 'luxon';

export const INCREASE_COLOR = '#2EA83A';
export const INCREASE_BG_COLOR = 'rgba(46, 168, 58, 0.1)';
export const DECREASE_COLOR = '#FF6F5B';
export const DECREASE_BG_COLOR = 'rgba(255, 111, 91, 0.1)';

export function emptyChartData(): ChartConfiguration['data'] {
  return {
    labels: [],
    datasets: [{ data: [], borderColor: '', backgroundColor: '' }],
  };
}

export const NPS_CHART_OPTIONS: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { display: false, grid: { display: false } },
    y: { display: false, grid: { display: false }, min: -100, max: 100 },
  },
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
    tooltip: { enabled: false },
  },
  elements: { line: { tension: 0.4 } },
};

export function buildPercentChartOptions(maxValue: number): ChartOptions {
  const chartMaxValue = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 100;

  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { display: false, grid: { display: false } },
      y: { display: false, grid: { display: false }, min: 0, max: chartMaxValue, beginAtZero: true },
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      datalabels: { display: false },
    },
    elements: { line: { tension: 0.4 } },
  };
}

type TimeSeriesPoint = { period: string; value: number | null };

export function padDateRange(
  periods: readonly TimeSeriesPoint[],
  startDate: string,
  endDate: string,
): TimeSeriesPoint[] {
  const valueByPeriod = new Map(periods.map(({ period, value }) => [period, value]));
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);
  const dayCount = Math.floor(end.diff(start, 'days').days) + 1;

  return Array.from({ length: dayCount }, (_, i) => {
    const period = start.plus({ days: i }).toFormat('yyyy-MM-dd');
    return { period, value: valueByPeriod.get(period) ?? null };
  });
}

export function fillTimeSeriesGaps(periods: readonly TimeSeriesPoint[]): TimeSeriesPoint[] {
  const lastRealIdx = periods.findLastIndex(({ value }) => value !== null);
  let prev: number | null = null;

  return periods.map(({ period, value }, i) => {
    const filled = value ?? (i <= lastRealIdx ? prev : null);
    if (value !== null) prev = value;
    return { period, value: filled };
  });
}

export function buildChartDataset(
  labels: string[],
  data: number[],
  borderColor: string,
  backgroundColor: string,
): ChartConfiguration['data'] {
  return {
    labels,
    datasets: [
      {
        data,
        borderColor,
        backgroundColor,
        borderWidth: 2,
        pointRadius: 0,
        fill: 'start',
      },
    ],
  };
}
